/**
 * Enhanced lesson markdown renderer.
 *
 * Wraps react-markdown with three rendering plugins that lift our
 * lessons above plain-text:
 *
 *   1. KaTeX — math formulas via `$inline$` and `$$display$$` blocks.
 *      remark-math parses, rehype-katex renders. Output is server-side
 *      friendly (just HTML + the KaTeX CSS) so no client-only quirks.
 *
 *   2. Mermaid — diagrams from ```mermaid``` code fences. Flowcharts,
 *      sequence diagrams, mind maps, state machines. Rendered into a
 *      sandboxed <div> with the official Mermaid renderer; we
 *      generate a stable ID per block so re-renders don't double-mount.
 *
 *   3. Chart — JSON data from ```chart``` code fences rendered with
 *      Recharts (already in the bundle). Keeps lessons interactive
 *      without us needing to author SVGs by hand. Schema:
 *         ```chart
 *         { "type": "bar"|"line"|"pie",
 *           "data": [{"name": "Q1", "value": 120}, ...],
 *           "xKey": "name",        // optional, defaults to "name"
 *           "yKey": "value" }      // optional, defaults to "value"
 *         ```
 *
 * Consumers (ExplainDeeperSheet, DoubtSidebar, ModuleModesModal) all
 * import this single component so the rendering stays consistent
 * everywhere a lesson body is displayed.
 *
 * Performance
 *   • Mermaid only initializes once (`mermaidReady` module-level guard).
 *   • Each Mermaid block renders independently and lazily.
 *   • KaTeX CSS is imported once and applies globally.
 */
import { Component, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import {
  Bar, BarChart, CartesianGrid, Cell,
  Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import "katex/dist/katex.min.css";

// ────────────────────────────────────────────────────────────────────
// Mermaid one-time init. Theme tuned for the dark glassmorphism UI.
//
// IMPORTANT: Mermaid v11 touches `document`/`window` at module-load
// time, which can throw when the bundle is evaluated inside a Sheet
// portal context on slower devices. We therefore:
//   • Lazy-import via dynamic import() so the chunk only loads on the
//     first MermaidBlock render.
//   • Catch ANY failure — if mermaid can't load (CSP, bundle parse
//     error, network), the diagram falls back to a styled code block
//     rather than crashing the whole reading pane.
// ────────────────────────────────────────────────────────────────────
let _mermaidModulePromise = null;
async function loadMermaid() {
  if (!_mermaidModulePromise) {
    _mermaidModulePromise = import("mermaid")
      .then((mod) => {
        const mermaid = mod.default || mod;
        mermaid.initialize({
          startOnLoad: false,
          theme: "dark",
          securityLevel: "loose",
          themeVariables: {
            fontFamily: "Inter, system-ui, sans-serif",
            background: "transparent",
            primaryColor: "#1a2236",
            primaryTextColor: "#e6edf6",
            primaryBorderColor: "#00FF9D",
            lineColor: "#5a7390",
            secondaryColor: "#16223a",
            tertiaryColor: "#0E1420",
          },
        });
        return mermaid;
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[LessonMarkdown] mermaid lazy-load failed", e);
        return null;
      });
  }
  return _mermaidModulePromise;
}

function MermaidBlock({ source }) {
  const ref = useRef(null);
  const [svg, setSvg] = useState("");
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mermaid = await loadMermaid();
      if (cancelled) return;
      if (!mermaid) { setErr("Diagram engine unavailable"); return; }
      // Mermaid requires a unique ID per render or it caches the SVG
      // against the previous source. Math.random keeps it cheap and
      // collision-free for the small N we render per lesson.
      const id = `oak-mmd-${Math.random().toString(36).slice(2)}`;
      try {
        const { svg: rendered } = await mermaid.render(id, source);
        if (!cancelled) setSvg(rendered);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Diagram render failed");
      }
    })();
    return () => { cancelled = true; };
  }, [source]);

  if (err) {
    return (
      <pre className="text-[11px] text-rose-300 bg-rose-950/30 border border-rose-500/30 rounded-lg p-3 my-3 overflow-x-auto">
        Mermaid error: {err}
        {"\n"}{source}
      </pre>
    );
  }
  return (
    <div
      ref={ref}
      data-testid="md-mermaid"
      className="my-5 p-4 rounded-xl border border-white/10 bg-white/[0.03] overflow-x-auto"
      // Mermaid renders trusted SVG (we control the source — it's our
      // own LLM output, gated through code fences). Safe to inject.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg || "<span style='color:#5a7390;font-size:11px'>Rendering diagram…</span>" }}
    />
  );
}

// ────────────────────────────────────────────────────────────────────
// Chart block — JSON-driven Recharts. Bar / line / pie supported.
// ────────────────────────────────────────────────────────────────────
const PIE_COLORS = ["#00FF9D", "#00C2FF", "#FF7A59", "#FFD166", "#A78BFA", "#F472B6"];

function ChartBlock({ source }) {
  let cfg;
  try {
    cfg = JSON.parse(source);
  } catch (e) {
    return (
      <pre className="text-[11px] text-rose-300 bg-rose-950/30 border border-rose-500/30 rounded-lg p-3 my-3 overflow-x-auto">
        Chart JSON parse error: {e.message}
      </pre>
    );
  }
  const data = Array.isArray(cfg.data) ? cfg.data : [];
  if (!data.length) return null;
  const xKey = cfg.xKey || "name";
  const yKey = cfg.yKey || "value";
  const type = (cfg.type || "bar").toLowerCase();

  const common = (
    <>
      <CartesianGrid stroke="#243049" strokeDasharray="3 3" />
      <XAxis dataKey={xKey} stroke="#7a8aa3" fontSize={11} />
      <YAxis stroke="#7a8aa3" fontSize={11} />
      <Tooltip contentStyle={{ background: "#0E1420", border: "1px solid #243049", fontSize: 12 }} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
    </>
  );

  return (
    <div
      className="my-5 p-4 rounded-xl border border-white/10 bg-white/[0.03]"
      style={{ height: 260 }}
      data-testid="md-chart"
    >
      <ResponsiveContainer width="100%" height="100%">
        {type === "line" ? (
          <LineChart data={data}>
            {common}
            <Line type="monotone" dataKey={yKey} stroke="#00FF9D" strokeWidth={2} dot />
          </LineChart>
        ) : type === "pie" ? (
          <PieChart>
            <Tooltip contentStyle={{ background: "#0E1420", border: "1px solid #243049", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Pie data={data} dataKey={yKey} nameKey={xKey} outerRadius={90} label>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
          </PieChart>
        ) : (
          <BarChart data={data}>
            {common}
            <Bar dataKey={yKey} fill="#00FF9D" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Custom <code>/<pre> component — intercepts mermaid + chart fences,
// passes everything else through to whatever the caller's
// `mdComponents` would render. Inline `code` stays untouched.
// ────────────────────────────────────────────────────────────────────
function makeCodeRenderer(userComponents = {}) {
  return function CodeRenderer({ inline, className, children, ...rest }) {
    const lang = /language-(\w+)/.exec(className || "")?.[1];
    const raw = String(children || "").replace(/\n$/, "");
    if (!inline && lang === "mermaid") return <MermaidBlock source={raw} />;
    if (!inline && lang === "chart") return <ChartBlock source={raw} />;
    // Defer to caller's renderer for everything else.
    if (userComponents.code) {
      return <userComponents.code inline={inline} className={className} {...rest}>{children}</userComponents.code>;
    }
    return inline ? (
      <code className={className} {...rest}>{children}</code>
    ) : (
      <pre className="bg-black/40 border border-white/10 rounded-xl p-3 overflow-x-auto my-4">
        <code className={className}>{children}</code>
      </pre>
    );
  };
}

/**
 * <LessonMarkdown markdown={…} components={…} />
 *
 * Drop-in replacement for `<ReactMarkdown>` in lesson-body contexts.
 * Accepts the same `components` prop as ReactMarkdown so callers can
 * still customise <h1>/<table>/<a>/etc. styling.
 *
 * Wrapped in an ErrorBoundary so a single bad KaTeX expression or
 * malformed Mermaid graph never blanks the entire reading pane.
 * Fallback renders plain ReactMarkdown (with GFM only) so the user
 * still sees lesson text even if the rich-rendering layer crashes.
 */
export default function LessonMarkdown({ markdown, components = {} }) {
  return (
    <_LessonMarkdownErrorBoundary markdown={markdown} components={components}>
      <_LessonMarkdownInner markdown={markdown} components={components} />
    </_LessonMarkdownErrorBoundary>
  );
}

function _LessonMarkdownInner({ markdown, components }) {
  const finalComponents = { ...components, code: makeCodeRenderer(components) };
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={finalComponents}
    >
      {markdown || ""}
    </ReactMarkdown>
  );
}

// Class component required for componentDidCatch — there is no hooks
// equivalent that catches render errors of children.
class _LessonMarkdownErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.warn("[LessonMarkdown] inner render crashed — falling back to plain markdown", err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={this.props.components}>
          {this.props.markdown || ""}
        </ReactMarkdown>
      );
    }
    return this.props.children;
  }
}
